<template>
    <DialogRoot v-model:open="open">
        <slot name="trigger" />

        <DialogPortal>
            <!--
                The backdrop is a separate element from the content on purpose: reka locks
                body scroll from DialogOverlay, not from DialogContent, so a lightbox that
                skipped the overlay would let the page behind it scroll under the photo.
            -->
            <DialogOverlay :class="element('backdrop')" />

            <DialogContent
                :class="classes"
                @keydown="onKeydown"
            >
                <!--
                    Both are required, not decorative: reka asserts in dev that the ids it
                    puts in aria-labelledby/aria-describedby resolve to real elements.
                -->
                <VisuallyHidden as-child>
                    <DialogTitle>{{ label }}</DialogTitle>
                </VisuallyHidden>

                <VisuallyHidden as-child>
                    <DialogDescription>{{ description }}</DialogDescription>
                </VisuallyHidden>

                <div :class="element('bar')">
                    <p
                        v-if="showCounter"
                        :class="element('counter')"
                        aria-hidden="true"
                    >
                        {{ index + 1 }} / {{ items.length }}
                    </p>

                    <DialogClose
                        :class="element('control', modifier('close'))"
                        :aria-label="closeLabel"
                    >
                        <span aria-hidden="true">
                            <slot name="close-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="M18 6 6 18M6 6l12 12" />
                                </svg>
                            </slot>
                        </span>
                    </DialogClose>
                </div>

                <!--
                    Clicking the empty space around the photo closes, which is what people
                    expect of a lightbox. `.self` keeps a click that landed on the photo or
                    a control from counting. reka's own dismiss-on-outside cannot do this:
                    the content fills the viewport, so nothing is ever "outside" it.
                -->
                <div
                    ref="stageRef"
                    :class="element('stage')"
                    @click.self="open = false"
                >
                    <button
                        v-if="showNavigation"
                        type="button"
                        :class="element('control', modifier('prev'))"
                        :disabled="!loop && index === 0"
                        :aria-label="previousLabel"
                        @click="go(-1)"
                    >
                        <span aria-hidden="true">
                            <slot name="prev-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="m15 18-6-6 6-6" />
                                </svg>
                            </slot>
                        </span>
                    </button>

                    <figure
                        v-if="current"
                        :class="element('figure')"
                        @click.self="open = false"
                    >
                        <slot
                            name="media"
                            :item="current"
                            :index="index"
                        >
                            <img
                                :src="current.src"
                                :alt="current.alt ?? ''"
                                :class="element('image')"
                            >
                        </slot>
                    </figure>

                    <button
                        v-if="showNavigation"
                        type="button"
                        :class="element('control', modifier('next'))"
                        :disabled="!loop && index === items.length - 1"
                        :aria-label="nextLabel"
                        @click="go(1)"
                    >
                        <span aria-hidden="true">
                            <slot name="next-icon">
                                <svg
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    stroke="currentColor"
                                    stroke-width="2"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                >
                                    <path d="m9 18 6-6-6-6" />
                                </svg>
                            </slot>
                        </span>
                    </button>
                </div>
            </DialogContent>
        </DialogPortal>
    </DialogRoot>
</template>

<script setup lang="ts">
import type { KMediaItem } from '../../types'
import type { KLightboxProps } from './types'
import { useSwipe } from '@vueuse/core'
import useBem from '@yeeroen/use-bem'
import {
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui'
import { computed, ref, watch } from 'vue'

defineOptions({ name: 'KLightbox' })

const props = withDefaults(defineProps<KLightboxProps>(), {
    items: () => [],
    label: 'Image viewer',
    description: 'Use the previous and next controls to move between images.',
    counter: true,
    loop: true,
    closeLabel: 'Close',
    previousLabel: 'Previous image',
    nextLabel: 'Next image',
})

defineSlots<{
    /**
     * Rendered inside the DialogRoot, so a `DialogTrigger` placed here gets reka's
     * open state and — the reason it matters — its return-focus wiring.
     */
    'trigger'?: () => unknown
    /** The full-size image. Falls back to a plain `<img>` built from `item.src`. */
    'media'?: (props: { item: KMediaItem, index: number }) => unknown
    'close-icon'?: () => unknown
    'prev-icon'?: () => unknown
    'next-icon'?: () => unknown
}>()

const open = defineModel<boolean>('open', { default: false })
const index = defineModel<number>('index', { default: 0 })

const stageRef = ref<HTMLElement | null>(null)

const { block, element, modifier } = useBem('k-lightbox')

const current = computed<KMediaItem | undefined>(() => props.items[index.value])

const showNavigation = computed(() => props.items.length > 1)
const showCounter = computed(() => props.counter && props.items.length > 1)

const classes = computed(() => block())

function go(step: number) {
    const total = props.items.length
    if (total < 2)
        return

    const next = index.value + step
    index.value = props.loop
        ? (next + total) % total
        : Math.min(Math.max(next, 0), total - 1)
}

// Scoped to the dialog rather than `document`: reka already owns Escape, and a document
// listener would keep firing for whatever else is on the page once this is closed.
function onKeydown(event: KeyboardEvent) {
    if (event.key === 'ArrowLeft')
        go(-1)
    else if (event.key === 'ArrowRight')
        go(1)
}

useSwipe(stageRef, {
    threshold: 40,
    onSwipeEnd(_event, direction) {
        if (direction === 'left')
            go(1)
        else if (direction === 'right')
            go(-1)
    },
})

// A shrinking list (a CMS preview editing the gallery live) would otherwise leave the
// index past the end and blank the stage.
watch(() => props.items.length, (total) => {
    if (total > 0 && index.value > total - 1)
        index.value = total - 1
})
</script>

<style lang="scss">
@use './k-lightbox.scss';
</style>
